import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../../../api/document';
import { hasEdited } from '../../../../../util/CurrentUser';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderMarkAsEditedButton } from '../../RenderLogic';

export const MarkAsEditedButton = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const { showToast } = useToastProvider();
  const { doneEditing } = useFlags();
  const [isMarkingAsEdited, setIsMarkingAsEdited] = useState(false);

  const commonToastProps = {
    id: 'mark-as-edited-toast',
    replace: true,
  };

  const handleMarkAsEdited = () => {
    setDisabled(true, setIsMarkingAsEdited);
    documentApi
      .editorDone(currentUser.companyId, qualioDocument.id)
      .then(() => {
        refetchDocumentOverviewData();
        setDisabled(false, setIsMarkingAsEdited);
        showToast({
          'data-cy': 'mark-as-edited-button-notify-success-toast',
          ...commonToastProps,
          description: t('success.yourEditHasBeenSubmitted'),
          status: 'success',
          title: t('notifications.success'),
        });
      })
      .catch((e) => {
        setDisabled(false, setIsMarkingAsEdited);
        const failureMessage =
          e.response?.data?.message ||
          t('documentActions.failedToMarkAsEdited');
        showToast({
          'data-cy': 'mark-as-edited-button-notify-error-toast',
          ...commonToastProps,
          status: 'error',
          title: t('notifications.error'),
          description: failureMessage,
        });
        setIsMarkingAsEdited(false);
      });
  };

  if (
    !doneEditing ||
    !shouldRenderMarkAsEditedButton(qualioDocument, currentUser)
  ) {
    return null;
  }

  return (
    <QButton
      isLoading={isMarkingAsEdited}
      variant="solid"
      onClick={handleMarkAsEdited}
      data-cy="doc-overview-mark-as-edited-button"
      isDisabled={
        hasEdited(currentUser, qualioDocument) ||
        isMarkingAsEdited ||
        isDisabled
      }
    >
      {t('buttons.markAsEdited')}
    </QButton>
  );
};
